import { Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { BigIntString } from "@smartrr/shared/entities/BigIntString";
import { ISmartrrFinancialStatus, ISmartrrOrderFromDb } from "@smartrr/shared/entities/Order";
import { formatMoney } from "@smartrr/shared/utils/formatMoney";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import groupBy from 'lodash/groupBy';
import React from "react";

import { usePurchaseStates } from "../../hooks/usePurchaseStates";
import { OrderHistoryPaymentInfo } from "./libs/components/OrderHistoryPaymentInfo";
import { OrderHistoryPricingInfo } from "./libs/components/OrderHistoryPricingInfo";
import { OrderHistoryOrderItems } from "./libs/components/OrderItems/OrderHistoryOrderItems";

export interface IOrderHistoryProps {
  order: ISmartrrOrderFromDb;
}

export const OrderDetailsModal = ({ order }: IOrderHistoryProps) => {
  const { orderCurrency } = order;

  const totalRefund = BigIntString.fromBigIntString(order.totalRefund);

  const itemsGroupedBySubscriptionsId = groupBy(
    order.items,
    ({ shopifySubscriptionId }) => shopifySubscriptionId
  );

  const purchaseStates = usePurchaseStates();

  return (
    <div className={prefixClassName("order-details")}>
      {Object.keys(itemsGroupedBySubscriptionsId).map((subscriptionId, index) => {
        const staleSubscription = order.sts.find(st => st.shopifyId === subscriptionId);
        const subscription = purchaseStates.find(st => st.id === staleSubscription?.id);
        return (
          <OrderHistoryOrderItems
            order={order}
            key={index}
            subscription={subscription}
            orderItems={itemsGroupedBySubscriptionsId[subscriptionId]}
            index={index}
          />
        );
      })}
      <OrderHistoryPricingInfo order={order} />
      {order.financialStatus === ISmartrrFinancialStatus.REFUNDED ? (
        <Stack py={2} spacing={1.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" whiteSpace="nowrap">
              {localize("orderHistory.refundAmount")}
            </Typography>
            <Typography variant="body1" sx={{ textAlign: "right" }}>
              {formatMoney(totalRefund ?? 0, orderCurrency)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" whiteSpace="nowrap">
              {localize("orderHistory.refundedTo")}
            </Typography>
            <OrderHistoryPaymentInfo order={order} />
          </Stack>
        </Stack>
      ) : (order.sts.length && order.sts[0].pmtMth) || order.pmtMthJson ? (
        <Stack spacing={1.5} pt={"15px"} className={prefixClassName("payment-information-line")}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" whiteSpace="nowrap" sx={{ fontWeight: "700 !important" }}>
              {localize("orderHistory.paid")}
            </Typography>
            <OrderHistoryPaymentInfo order={order} />
          </Stack>
        </Stack>
      ) : null}
    </div>
  );
};
